/**
 * Экран «Аналитика заявок» (manager v3 §6.3 «Аналитика заявок · SLA,
 * performance», route /requests/analytics).
 *
 * Дизайн (цитата макета):
 *   - topbar: крошки «Заявки / Аналитика» + h2 «Эффективность · <период>»
 *     + CTA «Период ▾» + «Экспорт»;
 *   - 8 KPI-плиток (grid-4 ×2): Всего заявок · Соблюдение норматива (SLA?) ·
 *     Среднее время реакции · Нарушения норматива · Медиана (P50?) ·
 *     95-й перцентиль (P95?) · Время до восстановления (MTTR?) ·
 *     Поток заявок (в час). Англ-термины с tooltip-расшифровкой на русском;
 *   - таблица «По отделам»: Отдел · Заявок · Среднее время · Соблюдение
 *     норматива (chip %);
 *   - таблица «Топ-исполнители»: аватар + имя + кол-во + среднее время;
 *   - период: день / неделя / месяц / произвольный; «Экспорт CSV»;
 *   - loading: skeleton плиток+таблиц; empty: «За период заявок не было».
 *
 * Все цифры — РЕАЛЬНЫЕ агрегаты GET /v1/requests/analytics (НЕ моки).
 * RBAC: только owner/manager (гард RequireManagementRole + бэк 403 гостю).
 */
import { useState } from 'react';
import { Card, Skeleton } from '@hsc/ui';
import { RequireManagementRole } from '../../shared/nav/RequireManagementRole.js';
import {
  downloadAnalyticsCsv,
  useRequestsAnalytics,
  type AnalyticsDept,
  type AnalyticsPeriodKey,
  type AnalyticsPerformer,
  type RequestsAnalytics,
} from './useRequestsAnalytics.js';

const PERIODS: ReadonlyArray<{ key: AnalyticsPeriodKey; label: string }> = [
  { key: 'day', label: 'День' },
  { key: 'week', label: 'Неделя' },
  { key: 'month', label: 'Месяц' },
];

/** «985» минут → «16 ч 25 мин»; до часа — «N мин». */
function fmtMin(mins: number): string {
  if (mins <= 0) return '0 мин';
  if (mins < 60) return `${mins} мин`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h} ч ${m} мин` : `${h} ч`;
}

/** Инициалы из имени для аватара исполнителя (как в §6.3 mockup). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0] ?? '';
  if (first === '') return '—';
  const second = parts[1];
  if (second === undefined) return first.slice(0, 2).toUpperCase();
  return ((first[0] ?? '') + (second[0] ?? '')).toUpperCase();
}

function slaChipClass(pct: number): string {
  if (pct >= 95) return 'chip ok';
  if (pct >= 85) return 'chip warn';
  return 'chip bad';
}

interface KpiProps {
  label: string;
  value: string;
  term?: { abbr: string; tip: string };
  sub?: string;
  testid: string;
}

function Kpi({ label, value, term, sub, testid }: KpiProps): JSX.Element {
  return (
    <div className="ra-kpi" data-testid={testid}>
      <div className="ra-kpi__label">
        {label}
        {term ? (
          <span className="ra-kpi__term" title={term.tip}>
            {term.abbr}
          </span>
        ) : null}
      </div>
      <div className="ra-kpi__value">{value}</div>
      {sub ? <div className="ra-kpi__sub">{sub}</div> : null}
    </div>
  );
}

function KpiGrid({ d }: { d: RequestsAnalytics }): JSX.Element {
  return (
    <div className="ra-kpi-grid" data-testid="ra-kpis">
      <Kpi testid="ra-kpi-total" label="Всего заявок" value={String(d.total)} />
      <Kpi
        testid="ra-kpi-sla"
        label="Соблюдение норматива"
        term={{ abbr: 'SLA?', tip: 'SLA — Service Level Agreement, договорённое время реакции' }}
        value={d.sla_measured > 0 ? `${d.sla_compliance_pct}%` : '—'}
      />
      <Kpi testid="ra-kpi-avg" label="Среднее время реакции" value={fmtMin(d.avg_response_min)} />
      <Kpi testid="ra-kpi-breaches" label="Нарушения норматива" value={String(d.breaches)} />
      <Kpi
        testid="ra-kpi-p50"
        label="Медиана"
        term={{ abbr: 'P50?', tip: 'P50 — медианное время: 50% заявок обслужены быстрее этой величины' }}
        value={fmtMin(d.p50_min)}
      />
      <Kpi
        testid="ra-kpi-p95"
        label="95-й перцентиль"
        term={{ abbr: 'P95?', tip: 'P95 — 95% заявок обслужены быстрее этой величины (включая «долгий хвост»)' }}
        value={fmtMin(d.p95_min)}
      />
      <Kpi
        testid="ra-kpi-mttr"
        label="Время до восстановления"
        term={{ abbr: 'MTTR?', tip: 'MTTR — Mean Time To Recovery, среднее время от поступления до завершения' }}
        value={fmtMin(d.mttr_min)}
      />
      <Kpi
        testid="ra-kpi-throughput"
        label="Поток заявок (в час)"
        value={String(d.throughput_per_hour)}
      />
    </div>
  );
}

function DeptTable({ rows }: { rows: AnalyticsDept[] }): JSX.Element {
  return (
    <Card>
      <h2 className="ra-card-title">По отделам</h2>
      <table className="ra-table" data-testid="ra-departments">
        <thead>
          <tr>
            <th>Отдел</th>
            <th>Заявок</th>
            <th>Среднее время</th>
            <th>Соблюдение норматива</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.department} data-testid={`ra-dept-${r.department}`}>
              <td>{r.department}</td>
              <td>{r.count}</td>
              <td>{fmtMin(r.avg_min)}</td>
              <td>
                {r.sla_compliance_pct === null ? (
                  <span className="ra-muted">—</span>
                ) : (
                  <span className={slaChipClass(r.sla_compliance_pct)}>{r.sla_compliance_pct}%</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function PerformersCard({ rows }: { rows: AnalyticsPerformer[] }): JSX.Element {
  return (
    <Card>
      <h2 className="ra-card-title">Топ-исполнители</h2>
      {rows.length === 0 ? (
        <p className="ra-muted">Назначений за период не было.</p>
      ) : (
        <ul className="ra-performers" data-testid="ra-performers">
          {rows.map((p) => (
            <li key={p.user_id} className="ra-performer" data-testid={`ra-performer-${p.user_id}`}>
              <span className="ra-performer__who">
                <span className="ra-avatar" aria-hidden="true">
                  {initials(p.name || '—')}
                </span>
                {p.name || 'Без имени'}
              </span>
              <span className="ra-performer__stat">
                <strong>{p.count}</strong> заявок · {fmtMin(p.avg_min)} среднее
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function AnalyticsBody(): JSX.Element {
  const [period, setPeriod] = useState<AnalyticsPeriodKey>('month');
  const [exporting, setExporting] = useState(false);
  const { data, isLoading, isError } = useRequestsAnalytics(period);

  const onExport = async (): Promise<void> => {
    setExporting(true);
    try {
      await downloadAnalyticsCsv(period);
    } finally {
      setExporting(false);
    }
  };

  return (
    <section data-testid="ra-page">
      <div className="ra-topbar">
        <div>
          <p className="ra-crumbs">Заявки / Аналитика</p>
          <h1 className="ra-title" data-testid="ra-heading">
            Эффективность{data ? ` · ${data.period.label}` : ''}
          </h1>
        </div>
        <div className="ra-actions">
          <div className="ra-period" role="group" aria-label="Период" data-testid="ra-period">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`ra-period__btn${period === p.key ? ' is-active' : ''}`}
                aria-pressed={period === p.key}
                data-testid={`ra-period-${p.key}`}
                onClick={() => setPeriod(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="ra-export"
            data-testid="ra-export"
            disabled={exporting || !data || data.total === 0}
            onClick={() => void onExport()}
          >
            {exporting ? 'Экспорт…' : 'Экспорт CSV'}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div data-testid="ra-loading">
          <div className="ra-kpi-grid">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} height={84} radius={16} />
            ))}
          </div>
          <div className="ra-tables" style={{ marginTop: '1rem' }}>
            <Skeleton height={220} radius={18} />
            <Skeleton height={220} radius={18} />
          </div>
        </div>
      ) : isError || !data ? (
        <Card>
          <p role="alert" data-testid="ra-error">
            Не удалось загрузить аналитику заявок. Попробуйте обновить страницу.
          </p>
        </Card>
      ) : data.total === 0 ? (
        <Card>
          <p className="ra-empty" data-testid="ra-empty">
            За период заявок не было.
          </p>
        </Card>
      ) : (
        <>
          <KpiGrid d={data} />
          <div className="ra-tables">
            <DeptTable rows={data.departments} />
            <PerformersCard rows={data.performers} />
          </div>
        </>
      )}
    </section>
  );
}

export function RequestsAnalyticsPage(): JSX.Element {
  return (
    <RequireManagementRole>
      <AnalyticsBody />
    </RequireManagementRole>
  );
}
